import React, { useEffect, useState } from "react";
import { ExternalLink, Loader2, CheckCircle2, XCircle, Clock, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const refresh = () => window.dispatchEvent(new Event("bfa:paypal-manual-refresh"));

export const PayPalMePayModal = ({ pay, onClose }) => {
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  if (!pay) return null;
  const amt = Number(pay.amount).toFixed(2);
  const claim = async () => {
    setClaiming(true);
    try {
      await api.post(`/billing/paypal-manual/${pay.session_id}/claim`);
      setClaimed(true);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record your payment");
    } finally { setClaiming(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" data-testid="paypal-me-modal">
      <div className="w-full max-w-md rounded-2xl border border-line bg-bg-surface p-6 relative">
        <button onClick={onClose} data-testid="paypal-me-close" className="absolute top-4 right-4 text-ink-muted hover:text-ink-primary transition">
          <X className="h-4 w-4" />
        </button>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-sky-300">// PAY WITH PAYPAL</div>
        {!claimed ? (
          <>
            <h3 className="font-display text-xl font-bold mt-1 text-ink-primary">Send ${amt} via PayPal</h3>
            <p className="text-xs text-ink-secondary mt-2 leading-relaxed">
              1. Tap the button below — PayPal opens with the amount pre-filled.<br />
              2. Complete the payment in PayPal.<br />
              3. Come back and hit <b>"I've sent the payment"</b>. The owner confirms receipt and your{" "}
              <span className="uppercase font-semibold text-ink-primary">{pay.plan_id}</span> plan ({pay.cycle}) activates.
            </p>
            <a
              href={pay.paypal_me_url} target="_blank" rel="noreferrer" data-testid="paypal-me-open-btn"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#0070ba] px-4 py-2.5 text-sm font-bold text-white hover:brightness-110 transition"
            >
              Open PayPal — pay ${amt} <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <button
              onClick={claim} disabled={claiming} data-testid="paypal-me-claim-btn"
              className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-2.5 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/20 transition disabled:opacity-50"
            >
              {claiming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              I've sent the payment
            </button>
          </>
        ) : (
          <div className="mt-3 text-center py-4" data-testid="paypal-me-claimed">
            <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
            <h3 className="font-display text-lg font-bold mt-2 text-ink-primary">Payment recorded</h3>
            <p className="text-xs text-ink-secondary mt-1">
              The station owner will confirm receipt shortly — your plan activates the moment it's approved.
            </p>
            <button
              onClick={onClose} data-testid="paypal-me-done-btn"
              className="mt-4 rounded-md border border-line bg-bg-elevated px-4 py-2 text-xs font-semibold text-ink-primary hover:bg-bg-surface transition"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const PayPalAwaitingBanner = () => {
  const [txs, setTxs] = useState([]);
  const load = () => api.get("/billing/paypal-manual/mine").then((r) => setTxs(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
    window.addEventListener("bfa:paypal-manual-refresh", load);
    return () => window.removeEventListener("bfa:paypal-manual-refresh", load);
  }, []);
  const claim = async (t) => {
    try {
      await api.post(`/billing/paypal-manual/${t.session_id}/claim`);
      toast.success("Recorded — awaiting owner confirmation");
      load();
      refresh();
    } catch { toast.error("Could not record payment"); }
  };
  if (!txs.length) return null;
  return (
    <div className="mb-6 rounded-xl border border-sky-500/40 bg-sky-500/5 p-4 space-y-2" data-testid="paypal-awaiting-banner">
      {txs.map((t) => (
        <div key={t.session_id} className="flex flex-wrap items-center gap-3 text-xs text-sky-100" data-testid={`paypal-awaiting-${t.session_id}`}>
          <Clock className="h-4 w-4 text-sky-300 shrink-0" />
          <span className="flex-1 min-w-[220px]">
            PayPal payment of <b>${Number(t.amount).toFixed(2)}</b> for the <b className="uppercase">{t.plan_id}</b> plan is{" "}
            {t.payment_status === "claimed"
              ? "awaiting the owner's confirmation — your plan activates once approved."
              : "started — finish paying on PayPal, then confirm below."}
          </span>
          {t.payment_status === "pending" && (
            <>
              <a
                href={`https://paypal.me/${t.paypal_me}/${Number(t.amount).toFixed(2)}USD`}
                target="_blank" rel="noreferrer" data-testid={`paypal-awaiting-open-${t.session_id}`}
                className="inline-flex items-center gap-1 rounded-md bg-[#0070ba] px-3 py-1.5 text-[11px] font-bold text-white hover:brightness-110 transition"
              >
                Open PayPal <ExternalLink className="h-3 w-3" />
              </a>
              <button
                onClick={() => claim(t)} data-testid={`paypal-awaiting-claim-${t.session_id}`}
                className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-[11px] font-semibold text-emerald-300 hover:bg-emerald-500/20 transition"
              >
                I've sent it
              </button>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export const PayPalAdminApprovals = ({ visible }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/billing/paypal-manual/pending").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => {
    if (!visible) return;
    load();
    window.addEventListener("bfa:paypal-manual-refresh", load);
    return () => window.removeEventListener("bfa:paypal-manual-refresh", load);
  }, [visible]);
  if (!visible || !rows || rows.length === 0) return null;
  const act = async (t, action) => {
    setBusy(t.session_id + action);
    try {
      await api.post(`/billing/paypal-manual/${t.session_id}/${action}`);
      toast.success(action === "approve" ? `Approved — ${t.email} is now on ${t.plan_id}` : "Payment rejected");
      load();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(""); }
  };
  return (
    <div className="mb-8 rounded-xl border border-brand-amber/40 bg-brand-amber/5 p-5" data-testid="paypal-admin-panel">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber">// OWNER APPROVAL</div>
      <h3 className="type-section-heading text-ink-primary mt-1 mb-3">PayPal.me payments awaiting confirmation</h3>
      <div className="space-y-2">
        {rows.map((t) => (
          <div key={t.session_id} className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-bg-elevated px-3 py-2.5 text-xs" data-testid={`paypal-admin-row-${t.session_id}`}>
            <span className={`rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider ${
              t.payment_status === "claimed" ? "bg-emerald-500/15 text-emerald-300" : "bg-sky-500/15 text-sky-300"
            }`}>
              {t.payment_status === "claimed" ? "buyer says paid" : "awaiting payment"}
            </span>
            <span className="text-ink-primary font-semibold">{t.email}</span>
            <span className="text-ink-secondary uppercase">{t.plan_id} · {t.cycle}</span>
            <span className="font-mono text-ink-primary">${Number(t.amount).toFixed(2)}</span>
            <span className="text-ink-muted font-mono text-[10px]">{new Date(t.created_at).toLocaleString()}</span>
            <span className="flex-1" />
            <button
              onClick={() => act(t, "approve")} disabled={!!busy} data-testid={`paypal-admin-approve-${t.session_id}`}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-3 py-1.5 text-[11px] font-bold text-black hover:brightness-110 transition disabled:opacity-50"
            >
              {busy === t.session_id + "approve" ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
              Approve & activate
            </button>
            <button
              onClick={() => act(t, "reject")} disabled={!!busy} data-testid={`paypal-admin-reject-${t.session_id}`}
              className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-[11px] font-semibold text-rose-300 hover:bg-rose-500/20 transition disabled:opacity-50"
            >
              {busy === t.session_id + "reject" ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
              Reject
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
